// ── THE CLAIM CROSS-CHECK ────────────────────────────────────────────────────
//
// Kevin, 2026-09-23: *"we have had AIPs make mistakes and we want to be able to catch
// those… All the cross check needs to do is identify a potential discrepancy and then we
// take it from there."* And on output: *"we don't need notifications from there, we just
// need to show when a discrepancy exists and let us figure it out."*
//
// So this FLAGS and a human adjudicates. It states both figures, names both sources, and
// says **discrepancy** — never "underpaid", which is a conclusion it cannot reach.
//
// ⚠ `m13_loss_detail` IS THE AIP'S OWN P21 CLAIM WORKSHEET, AND NOTHING HAS EVER READ IT.
// 4,173 rows written by api/_m13mirror.js, read by zero lines of app code until now. That
// is what makes an immediate cross-check possible with no new data and no waiting: the
// carrier states, on its own record, what it guaranteed and what it paid. Where those two
// disagree the carrier's own worksheet disagrees with itself.
//
// ── WHAT IS CHECKED, AND WHY ONLY THIS ───────────────────────────────────────
// Two identities, both measured over the live table on 2026-09-23 rather than assumed:
//
//   1. A PREVENT-PLANT LINE PAYS ITS STATED GUARANTEE. There is no production to count on
//      prevented acres, so the payment IS the guarantee. Holds on **811 of 850** NAU PP
//      lines (stages PF · P2 · P); the other **39** are the worklist.
//   2. THE AIP'S TWO OWN FIGURES AGREE — `preliminary_indemnity` against `aip_indemnity`.
//      Holds on **3,668 of 3,670** rows that carry both. The 2 that do not are a carrier
//      contradicting its own worksheet, which needs no model of ours at all.
//
//   3. A HARVEST LINE PAYS ITS STATED UNIT DEFICIENCY — `unit_deficiency` against
//      `aip_indemnity`. Holds on **889 of 964** NAU H+UH lines, and the total gap across
//      all 889 is **$217**. The 75 that do not are reported as NOT CHECKABLE with their
//      ratio, not as discrepancies — see the harvest branch for why.
//
// ⚠ A HARVEST LINE IS NOT CHECKED AGAINST THE GUARANTEE. There, paid is guarantee MINUS
// production to count, so paid < guarantee is the NORMAL case — measured 2026-09-28,
// `paid = guarantee` holds on 0 of 890 H lines and that is correct, not a finding.
// Flagging those would bury the real ones under 890 false ones, which is how a worklist
// gets ignored.
//
// ⚠⚠ AND THE FIRST READING OF `unit_deficiency` WAS WRONG, WHICH COST 26% OF THE BOOK.
// This header used to say: "`unit_deficiency` looked like the missing term and is not:
// `paid = guarantee − deficiency` holds on 0 of 890. The harvest identity is NOT
// ESTABLISHED." The measurement was right and the identity tested was the wrong one —
// the deficiency is not a term to subtract, it IS the indemnity. So 964 lines sat behind
// an honest "we cannot check this" that was honestly mistaken about its own reach. A
// measurement that refutes one identity says nothing about a different one.
//
// ⚠ `stage_percent_factor` IS 0 ON ALL 3,763 NAU ROWS AND ALL 410 R&H ROWS. Do not build
// on it. It is the obvious field for a stage-scaled guarantee and it is empty on both
// carriers, which is exactly the shape of a column that reads as "no reduction" when it
// means "never populated".

import { PP_LINE_STAGES, isPreventPlantStage as isPpStageShared } from "./ppStages";

export const STAGE = Object.freeze({
  // Prevent plant: the payment is the guarantee, so the identity holds.
  // The ONE shared set (src/claims/ppStages.js). `P` is RMA's "Uninsured Loss", not PP.
  PREVENT_PLANT: PP_LINE_STAGES,
  // Harvest and its production-to-count offset. Checked only for self-agreement.
  HARVEST: ["H", "UH"],
});

export const VERDICT = Object.freeze({
  AGREES: "agrees",
  DISCREPANCY: "discrepancy",
  NOT_CHECKABLE: "not_checkable",
});

// ⚠ A CENT TOLERANCE, NOT A MATERIALITY THRESHOLD. There is no dollar floor here on
// purpose: Kevin asked for no notifications, so nothing fires on volume and nothing has to
// be suppressed to keep an inbox usable. Measured, the whole book produces **41 flagged
// lines out of 4,173** — a worklist short enough to read, ranked by dollars. Inventing a
// "major" cutoff would silently drop the small ones, and a $25 line the carrier cannot
// explain is still a carrier that cannot explain a line.
const TOL = 1; // one dollar — below this is rounding in the file, not a disagreement

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const up = (s) => String(s == null ? "" : s).trim().toUpperCase();

export const isPreventPlantStage = (code) => isPpStageShared(code);
export const isHarvestStage = (code) => STAGE.HARVEST.includes(up(code));

// ── ONE LINE OF THE AIP'S WORKSHEET ──────────────────────────────────────────
// Returns every check that could be run on this row, each with its own verdict, plus the
// row's overall verdict. A row where nothing could be run is NOT_CHECKABLE with the reason
// — never AGREES, because a silent pass is the one output that makes the tool worse than
// nothing when its whole purpose is catching mistakes.
export function crossCheckLine(row) {
  const r = row || {};
  const paid = num(r.aip_indemnity);
  const guarantee = num(r.loss_guarantee);
  const prelim = num(r.preliminary_indemnity);
  // The carrier's own deficiency on the unit. On a HARVEST line this is the indemnity —
  // see the harvest branch below, where the identity and its limits are measured.
  const deficiency = num(r.unit_deficiency);
  const stage = up(r.stage_code);
  const checks = [];

  // 1. the carrier against itself
  if (paid != null && prelim != null) {
    const diff = paid - prelim;
    checks.push({
      id: "prelim-vs-paid",
      label: "The carrier's own preliminary figure against what it paid",
      a: { label: "preliminary indemnity", value: prelim, source: "AIP P21 worksheet" },
      b: { label: "paid", value: paid, source: "AIP P21 worksheet" },
      diff, verdict: Math.abs(diff) <= TOL ? VERDICT.AGREES : VERDICT.DISCREPANCY,
    });
  } else {
    checks.push({
      id: "prelim-vs-paid", label: "The carrier's own preliminary figure against what it paid",
      verdict: VERDICT.NOT_CHECKABLE,
      reason: prelim == null ? "this carrier states no preliminary indemnity on this record"
                             : "no payment is recorded on this line yet",
    });
  }

  // 2. a prevent-plant line pays its stated guarantee
  if (isPreventPlantStage(stage)) {
    if (paid != null && guarantee != null) {
      const diff = paid - guarantee;
      checks.push({
        id: "pp-pays-guarantee",
        label: "Prevented-plant payment against the guarantee the carrier stated",
        a: { label: "stated guarantee", value: guarantee, source: "AIP P21 worksheet" },
        b: { label: "paid", value: paid, source: "AIP P21 worksheet" },
        diff, ratio: guarantee !== 0 ? paid / guarantee : null,
        verdict: Math.abs(diff) <= TOL ? VERDICT.AGREES : VERDICT.DISCREPANCY,
      });
    } else {
      checks.push({
        id: "pp-pays-guarantee",
        label: "Prevented-plant payment against the guarantee the carrier stated",
        verdict: VERDICT.NOT_CHECKABLE,
        // ⚠ THIS IS THE WHOLE R&H BOOK. Measured: R&H populates loss_guarantee on 0 of 410
        // rows — it sends aip_indemnity and determined_acreage and nothing else — so there
        // is no stated guarantee to compare a payment against. Saying so is the point; a
        // carrier we cannot check must not read as a carrier that checked out.
        reason: guarantee == null
          ? "this carrier does not state a guarantee on its claim record, so there is nothing to compare the payment against"
          : "no payment is recorded on this line yet",
      });
    }
  } else if (isHarvestStage(stage)) {
    // ── A HARVEST LINE PAYS ITS STATED UNIT DEFICIENCY ────────────────────────
    // ⚠ CORRECTED 2026-09-28. This branch read NOT CHECKABLE on the reasoning that
    // "`unit_deficiency` looked like the missing term and is not: `paid = guarantee −
    // deficiency` holds on 0 of 826". That measurement was right and it tested the wrong
    // identity. `unit_deficiency` IS the indemnity, not something to subtract from the
    // guarantee — measured over the live worksheet 2026-09-28:
    //
    //     paid = guarantee − deficiency      0 of 890 H lines
    //     paid = guarantee                   0 of 890 H lines
    //     paid = deficiency                889 of 964 H+UH lines   ← and the total gap
    //                                                                across all 889 is $217.15
    //
    // So 964 NAU lines — 26% of the worksheet — were being reported as uncheckable when
    // the carrier's own two figures can in fact be compared. Fire Creek's own harvest
    // lines are exact: paid 3,449 against deficiency 3,448.95; 3,140 against 3,140.12.
    //
    // ⚠ AND IT IS NOT VACUOUS, WHICH WAS THE FIRST THING TO RULE OUT. If the carrier
    // simply copied one field into the other it would hold on 100%, not 92%, and the 8%
    // that fail do so in structured ways (below). A line where a carrier's own deficiency
    // and its own payment disagree is exactly what this tool exists to surface.
    if (paid == null || deficiency == null) {
      checks.push({
        id: "harvest-pays-deficiency", label: "Payment against the stated unit deficiency",
        verdict: VERDICT.NOT_CHECKABLE,
        // ⚠ THE REASON DESCRIBES THIS LINE, AND ONLY THEN THE CARRIER. An earlier draft
        // said "this carrier does not state a unit deficiency", which is a claim about
        // R&H printed on any NAU line that happens to be missing one.
        reason: paid == null
          ? "no payment is recorded on this line yet"
          : "no unit deficiency is stated on this line, so there is nothing to compare the "
          + "payment against — R&H states one on 0 of its 420 worksheet lines, so its "
          + "harvest lines are never checkable this way",
      });
    } else if (Math.abs(paid - deficiency) <= TOL) {
      checks.push({
        id: "harvest-pays-deficiency", label: "Payment against the stated unit deficiency",
        verdict: VERDICT.AGREES,
        a: { value: deficiency, source: "unit deficiency, per the carrier's P21 worksheet" },
        b: { value: paid, source: "indemnity paid, same worksheet" },
        diff: paid - deficiency,
      });
    } else {
      // ⚠ A DISAGREEMENT HERE IS REPORTED AS NOT CHECKABLE, NOT AS A DISCREPANCY, AND
      // THAT IS THE CONSERVATIVE READING RATHER THAN A HEDGE. `unit_deficiency` is not
      // always stated in dollars, and the file never says which unit it is in. Measured
      // over the 75 lines that do not agree: 36 sit at a constant 18.15x across four
      // claim-stages (the same constant this file already records for `loss_guarantee`),
      // and the rest run from 0.29x to 430x — ratios that look like a price per bushel or
      // a guarantee per acre, not like a carrier paying the wrong amount.
      //
      // Calling those a discrepancy would put ~$1.6M of "unexplained" money on a page
      // whose credibility is the whole product, most of it a unit conflation rather than
      // a carrier error. Calling them uncheckable, WITH THE RATIO PRINTED, says the true
      // thing: these two figures are not the same kind of number and a human can see why
      // at a glance. Where the ratio IS plausible as a payment difference it still reads
      // as an open question rather than a settled one.
      //
      // The bar for promoting these to discrepancies is a source for what unit the
      // deficiency is in — the AIP, or its own statement — not a threshold we invent.
      const ratio = deficiency !== 0 ? paid / deficiency : null;
      checks.push({
        id: "harvest-pays-deficiency", label: "Payment against the stated unit deficiency",
        verdict: VERDICT.NOT_CHECKABLE,
        a: { value: deficiency, source: "unit deficiency, per the carrier's P21 worksheet" },
        b: { value: paid, source: "indemnity paid, same worksheet" },
        diff: paid - deficiency,
        reason: "paid is " + (ratio == null ? "set against a zero deficiency"
                : ratio.toFixed(2) + "x the stated unit deficiency")
              + " — the carrier does not state which unit that figure is in, and it is not "
              + "always dollars, so the two are not comparable on this line",
      });
    }
  } else {
    // ── A STAGE WITH NO ESTABLISHED IDENTITY SAYS SO, OUT LOUD ────────────────
    // ⚠ THIS BRANCH EXISTS BECAUSE ITS ABSENCE WAS A FALSE ALL-CLEAR ON 44% OF THE
    // WORKSHEET. `FL` is the single biggest stage in the table — 1,856 NAU lines and 189
    // R&H, 2,045 of 4,183 — and no branch reached it, so an FL line's ONLY check was
    // prelim-vs-paid. That check compares the carrier's preliminary figure with its final
    // one and, on FL, agrees on 1,856 of 1,856. So every FL line rendered a clean AGREES
    // having had nothing substantive compared at all: measured, a line paying $8 against
    // its own stated $1,313 guarantee reads "agrees" today.
    //
    // ⚠ AND THE FIX IS NOT AN IDENTITY, BECAUSE THERE ISN'T ONE. Measured over the 1,856
    // FL lines: `paid = guarantee` holds on 1,484 (80%) and the rest run from 0.006x to
    // 1.000x — a bound, not an identity. Flagging the 372 would be the cry-wolf failure
    // this module opens with. `acre_stage_guarantee`, which every other stage derives its
    // guarantee from (1,902 of 1,906 lines: guarantee = asg x acres), is populated on just
    // 43 of 1,856 FL lines — consistent with FL being an area product with no per-acre unit
    // guarantee, which is what memory records for it, and NOT established from the file.
    //
    // So the honest output is the one this module already has a verdict for: the question
    // is open, it is COUNTED in `totals.unresolved`, and it is named on screen. Turning a
    // silent pass into a stated gap changes no dollar and removes a false assurance.
    if (paid != null) {
      checks.push({
        id: "stage-identity", label: "Payment against a rule for this stage",
        verdict: VERDICT.NOT_CHECKABLE,
        // The code renders RAW — the same convention the claim stage codes and the
        // insurance option codes already follow. A stage the carrier starts sending
        // appears here on its own rather than being folded into a neighbour.
        reason: "no payment rule is established for stage " + (stage || "(not stated)")
              + " from what the carrier sends, so the payment on this line has not been "
              + "checked against anything except the carrier's own preliminary figure",
      });
    }
  }

  const any = (v) => checks.some((c) => c.verdict === v);
  return {
    key: r.loss_detail_key || r.id || null,
    aip: r.aip || null, claimNumber: r.claim_number || null, cropYear: num(r.crop_year),
    // ⚠ THE CLAIM NUMBER IS NOT THE CLAIM — see crossCheckBook. `producer_key` is the
    // carrier's own insured key and `iif_key` its coverage key; both are needed to say
    // WHOSE line this is, and neither is derivable from the number.
    producerKey: r.producer_key == null ? null : String(r.producer_key),
    iifKey: r.iif_key == null ? null : String(r.iif_key),
    stage, acres: num(r.determined_acreage),
    paid, guarantee, prelim,
    checks,
    verdict: any(VERDICT.DISCREPANCY) ? VERDICT.DISCREPANCY
           : any(VERDICT.AGREES) ? VERDICT.AGREES : VERDICT.NOT_CHECKABLE,
    // The biggest unexplained dollar gap on the line, for ranking. Never a severity grade:
    // the tool does not decide which discrepancies matter.
    worstDiff: checks.filter((c) => c.verdict === VERDICT.DISCREPANCY)
      .reduce((m, c) => (Math.abs(c.diff) > Math.abs(m) ? c.diff : m), 0),
  };
}

// ── THE WHOLE BOOK ───────────────────────────────────────────────────────────
// Rolls the lines up per CLAIM, because a claim is what an agent picks up the phone about.
// Counts are reported at both grains so a screen can say "3 claims, 13 lines" rather than
// implying thirteen conversations.

// ── A CONSTANT RATIO ACROSS A WHOLE STAGE IS A UNIT MISMATCH, NOT N ERRORS ───
//
// Measured on the live worksheet 2026-09-24. Claim 00000202 CY2025 carries ten P2 lines
// whose paid amount is 18x the stated guarantee, and claim 00000201 carries three more:
//
//     guarantee    paid      ratio          guarantee    paid      ratio
//         1,272  23,087    18.1502                171   3,104    18.1520
//           547   9,928    18.1499              2,199  39,912    18.1501
//           373   6,770    18.1501                691  12,542    18.1505
//
// Thirteen lines, spanning a 13x range of guarantee sizes, landing on ONE constant to
// four significant figures — and the SAME constant on two different claims. Thirteen
// independent payment errors do not do that. A price does.
//
// So `loss_guarantee` on those lines is stated in PRODUCTION UNITS, not dollars, and
// comparing it to a dollar payment is the unit-of-measure conflation this codebase
// forbids everywhere else. Corroborating: `acre_stage_guarantee` there is 60-66 per
// acre, which is not a credible dollar guarantee on a crop paying ~$1,196/ac; and P11
// field 117 is already recorded as carrying dollars on some rows and a quantity on
// others, one column with two units.
//
// ⚠ THE BAR IS DELIBERATELY ONE-SIDED. Only a constant ABOVE 1 is treated this way — a
// carrier does not pay thirteen lines at 18x by mistake. A constant BELOW 1 is exactly
// what a systematic underpayment looks like and MUST stay on the worklist: claim
// 00000066 has ten P2 lines at a constant 0.29x, and that is the shape this tool exists
// to surface, not to explain away.
//
// ⚠ AND IT REFUSES, IT DOES NOT ABSOLVE. These become NOT CHECKABLE with the reason
// stated — the honest claim is "our comparison is invalid on these rows", never "the
// carrier is right".
export const SCALE_MISMATCH = Object.freeze({
  // Two lines can share a ratio by chance; three at four significant figures cannot.
  MIN_LINES: 3,
  // The observed spread is 0.012%. Half a percent is forty times looser and still far
  // tighter than any set of independent errors.
  MAX_REL_SPREAD: 0.005,
  // Paid at least double the stated guarantee — see the one-sided note above.
  MIN_RATIO: 2,
});

function applyScaleMismatch(lines) {
  const byStage = new Map();
  for (const l of lines) {
    if (l.verdict !== VERDICT.DISCREPANCY) continue;
    for (const c of l.checks || []) {
      if (c.verdict !== VERDICT.DISCREPANCY) continue;
      if (!c.a || !c.b || !(c.a.value > 0) || !(c.b.value > 0)) continue;
      // Keyed by the INSURED as well as the number: one claim number can carry two
      // different insureds (see crossCheckBook), and pooling their lines would let one
      // farm's unit mismatch mute the other farm's real discrepancy.
      const k = [l.aip, l.cropYear, l.claimNumber, l.producerKey, l.stage].join("|");
      if (!byStage.has(k)) byStage.set(k, []);
      byStage.get(k).push({ line: l, ratio: c.b.value / c.a.value });
    }
  }
  const hits = [];
  for (const group of byStage.values()) {
    if (group.length < SCALE_MISMATCH.MIN_LINES) continue;
    const rs = group.map((x) => x.ratio);
    const mean = rs.reduce((a, b) => a + b, 0) / rs.length;
    if (!(mean >= SCALE_MISMATCH.MIN_RATIO)) continue;
    if (!((Math.max(...rs) - Math.min(...rs)) / mean <= SCALE_MISMATCH.MAX_REL_SPREAD)) continue;
    for (const x of group) {
      x.line.verdict = VERDICT.NOT_CHECKABLE;
      x.line.worstDiff = 0;
      x.line.scaleMismatch = { ratio: mean, lines: group.length };
      x.line.reason = "paid is a constant " + mean.toFixed(2) + "x the stated guarantee on all "
        + group.length + " lines of this stage — the guarantee on these lines is not stated in "
        + "dollars, so the two figures are not comparable";
      for (const c of x.line.checks || []) if (c.verdict === VERDICT.DISCREPANCY) c.verdict = VERDICT.NOT_CHECKABLE;
      hits.push(x.line);
    }
  }
  return hits;
}

// ⚠ A WORKSHEET LINE THAT CANNOT NAME ITS CROP READS AS BELONGING TO WHATEVER THE
// READER LAST LOOKED AT. Kevin, 2026-10-01: *"Sullivan Wheat claim was a harvest claim,
// not a PP claim, important distinction."* He was right, and the screen had misled him:
// Sullivan holds TWO CY2026 claims — 00000047 (WHEAT, every P21 line stage `H`) and
// 00000066 (dry beans / grain sorghum / soybeans, stage `P2`). The cross-check printed
// the 00000066 worksheet lines as "Claim 00000066 · P2 · 37 ac" with NO crop and NO
// county, directly beneath a cell list that includes MONROE · WHEAT. Read down the
// column, the prevent-plant lines look like the wheat claim's.
//
// `m13_loss_detail` carries no crop and no county — only `iif_key`, the carrier's own
// coverage key. `crop_coverage` carries that key too, so the crop is one join away:
// measured 2026-10-01, 407 of 409 CY2026 worksheet lines resolve from the coverage rows
// this screen ALREADY loads. (Book-wide it is 17%, because crop_coverage holds current
// crop years and the worksheet reaches back further — which is why an unresolved line
// prints nothing rather than borrowing a neighbour's crop.)
export function crossCheckBook(rows, { cropYear = null, aip = null, covByIif = null } = {}) {
  const src = (rows || []).filter((r) =>
    (cropYear == null || num(r.crop_year) === num(cropYear)) &&
    (aip == null || r.aip === aip));
  const lines = src.map((r) => {
    const l = crossCheckLine(r);
    const c = covByIif && l.iifKey ? covByIif.get(String(l.iifKey)) : null;
    // Null, never a guess. A line whose coverage we cannot resolve says nothing about
    // its crop — which is the honest version of the defect this fixes.
    l.crop = (c && c.crop) || null;
    l.county = (c && c.county) || null;
    return l;
  });
  // Re-mark whole stages whose "discrepancy" is a unit mismatch — see SCALE_MISMATCH.
  applyScaleMismatch(lines);

  // ⚠ A CLAIM NUMBER IS NOT A CLAIM. The carrier reuses one number across two DIFFERENT
  // insureds — measured on the live worksheet 2026-09-24, 29 of 425 NAU claim numbers
  // carry two `producer_key`s, and keyed on the number alone those 29 collapse into one
  // row apiece: two farms' lines summed into one "paid", one verdict covering both, and
  // a farm name that could only ever be a tie-break between them. Keyed on the insured
  // as well there are 454 claims, and 25 of the 29 split into two plainly different
  // farms (the other 4 are one farm holding two insured keys — MEISENZAHL FARMS LLC,
  // RICHARD PADGHAM, SHAFFER PRODUCTIONS INC, EDWIN O BLODGETT II — which the sharper
  // key correctly keeps apart as two claims rather than inventing a second farm).
  //
  // This is the same rule `loss_key IS the claim` already records one level up: the
  // number is a LABEL the carrier prints, not a key we may group on.
  const byClaim = new Map();
  for (const l of lines) {
    const k = [l.aip, l.cropYear, l.claimNumber, l.producerKey].join("|");
    if (!byClaim.has(k)) byClaim.set(k, {
      key: k, aip: l.aip, cropYear: l.cropYear, claimNumber: l.claimNumber,
      producerKey: l.producerKey, iifKeys: [],
      lines: [], flagged: [], paid: 0, worstDiff: 0,
    });
    const c = byClaim.get(k);
    if (l.iifKey && !c.iifKeys.includes(l.iifKey)) c.iifKeys.push(l.iifKey);
    c.lines.push(l);
    c.paid += l.paid || 0;
    if (l.verdict === VERDICT.DISCREPANCY) {
      c.flagged.push(l);
      if (Math.abs(l.worstDiff) > Math.abs(c.worstDiff)) c.worstDiff = l.worstDiff;
    }
  }
  const claims = [...byClaim.values()].map((c) => ({
    ...c,
    verdict: c.flagged.length ? VERDICT.DISCREPANCY
      : c.lines.some((l) => l.verdict === VERDICT.AGREES) ? VERDICT.AGREES : VERDICT.NOT_CHECKABLE,
  })).sort((a, b) => Math.abs(b.worstDiff) - Math.abs(a.worstDiff));

  // ⚠ NOT-CHECKABLE IS COUNTED AND SHOWN, NOT NETTED AWAY. "1,412 lines we could not check"
  // is a fact about this tool's reach, and a screen that printed only the flags would read
  // as a clean book — which is exactly the false all-clear this tool exists to avoid.
  const tally = (v) => lines.filter((l) => l.verdict === v).length;
  // ⚠ A LINE'S VERDICT IS "DID ANYTHING AGREE", SO AN UNRESOLVED CHECK CAN HIDE BEHIND A
  // RESOLVED ONE — and adding the harvest identity made that live rather than theoretical.
  // A harvest line whose payment and stated deficiency are not comparable still carries an
  // AGREEING prelim-vs-paid check beside it, so the LINE reads `agrees` and would appear
  // in neither the worklist nor the not-checkable count. 75 lines would simply vanish,
  // which is the one thing this file's own rule forbids: not-checkable is counted and
  // shown, never netted away. Counted at the CHECK grain, so nothing can hide.
  const unresolvedLines = lines.filter((l) =>
    (l.checks || []).some((c) => c.verdict === VERDICT.NOT_CHECKABLE)).length;
  return {
    claims, lines,
    totals: {
      lines: lines.length, claims: claims.length,
      discrepancy: tally(VERDICT.DISCREPANCY),
      agrees: tally(VERDICT.AGREES),
      notCheckable: tally(VERDICT.NOT_CHECKABLE),
      // Lines carrying at least one question nothing could settle — including those whose
      // line-level verdict is `agrees` on a DIFFERENT check. Always >= notCheckable.
      unresolved: unresolvedLines,
      claimsFlagged: claims.filter((c) => c.verdict === VERDICT.DISCREPANCY).length,
      dollars: lines.reduce((s, l) => s + Math.abs(l.worstDiff), 0),
    },
    // Per carrier, because the reach differs so much between them that one number would
    // misdescribe both. Measured: NAU states a guarantee on every row, R&H on none.
    byAip: [...new Set(lines.map((l) => l.aip))].filter(Boolean).sort().map((a) => {
      const sub = lines.filter((l) => l.aip === a);
      return { aip: a, lines: sub.length,
        discrepancy: sub.filter((l) => l.verdict === VERDICT.DISCREPANCY).length,
        notCheckable: sub.filter((l) => l.verdict === VERDICT.NOT_CHECKABLE).length };
    }),
  };
}

// ── OUR OWN ESTIMATE, AGAINST WHAT ARRIVED ───────────────────────────────────
//
// Kevin, 2026-09-23: *"Yes we need to estimate before it's paid, that's the whole point of us
// doing this. We will calculate what we think the claim should be and then see what actually
// arrives when the claim is processed and paid by AIP."*
//
// ⚠ AN ESTIMATE THAT DID NOT PREDATE THE PAYMENT IS NOT A FORECAST, AND THIS REFUSES TO
// PRESENT IT AS ONE. `claim_estimates.payment_known_at_estimate` is written at insert from
// what the claim worksheet held at that moment, so the row itself carries the answer. A
// comparison built on a row where money had already arrived is returned with
// `forecast: false` and a reason — it can still be informative, but it cannot be evidence
// that we called it, and a tool whose purpose is "we called this and they got it wrong"
// stops being worth anything the moment those two are mixed.
//
// ⚠ AND A MISSING PAYMENT IS NOT A ZERO. A claim the AIP has not settled yet has nothing to
// compare against; that is PENDING, not agreement and not a discrepancy. Treating an absent
// payment as $0 would report every open claim as a total underpayment.
export const ESTIMATE = Object.freeze({
  PENDING: "pending",            // we have an estimate; the carrier has not paid yet
  AGREES: "agrees",
  DISCREPANCY: "discrepancy",
  NOT_A_FORECAST: "not_a_forecast", // the estimate was written after the money arrived
  NO_ESTIMATE: "no_estimate",    // paid, and we never said what we expected
});

// ⚠ A MATERIALITY FLOOR EXISTS HERE AND DELIBERATELY NOT ON THE WORKSHEET CHECK. There, both
// figures come from ONE document and any disagreement is the carrier contradicting itself, so
// a dollar matters. Here one side is OUR model of a claim — it will never land to the cent, and
// a rule that flagged every cent would flag every claim and be ignored within a week. BOTH
// terms must be met: the percentage stops a small claim flagging on rounding, and the dollar
// floor stops a trivial gap on a large one.
//
// ⚠ THESE TWO NUMBERS ARE A STATED JUDGEMENT, NOT A MEASUREMENT, AND THE SCREEN SAYS SO.
// The right way to set them is off the distribution of real estimate-vs-paid gaps — and that
// distribution does not exist yet, because no estimate has ever been recorded: this table is
// new and every row in it will be written from today forward. Deriving a threshold from an
// empty history would be an invented number wearing a measurement's clothes, so they are
// declared, printed with every verdict, and meant to be revisited once there are enough
// closed comparisons to fit them to. Until then a reader sees the raw dollars and percentage
// on every row and can disagree with the label.
//
// ⚠ AND THE `AND` IS THE CONSERVATIVE CHOICE, WHICH IS THE RIGHT ERROR HERE. A 2% gap on a
// $1M claim is $20,000 and reads as agreement under this rule. That is deliberate while our
// own estimator is unproven: over-flagging trains a reader to skip the list, and the list is
// the entire product. Revisit this alongside the thresholds, against real gaps.
//
// ⚠⚠ SUPERSEDED 2026-10-19 BY KEVIN'S APPROVED RULE, AND IT IS NO LONGER AN `AND`.
// The roadmap decision: an estimate and a payment disagree when the gap exceeds THE GREATER
// OF $25 OR 1% of the estimate, per unit (per frozen estimate). The 5%-and-$500 pair above
// read a $20,000 gap on a $1M claim as agreement; under the approved rule that is 2% and
// flags. `estimateFlagThreshold` is the ONE function every estimate-vs-paid comparison uses.
export const ESTIMATE_TOLERANCE = Object.freeze({ pct: 0.01, dollars: 25 });

// Flag when |paid - estimate| is STRICTLY GREATER than max($25, 1% of |estimate|). A gap
// exactly at the threshold is within it. Run in integer cents so $25.00 never reads as
// $25.000000000001 — the boundary is the whole contract of this function.
export function estimateFlagThreshold(estimate, diff, tol = ESTIMATE_TOLERANCE) {
  const estC = Math.abs(Math.round((num(estimate) || 0) * 100));
  const diffC = Math.abs(Math.round((num(diff) || 0) * 100));
  const floorC = Math.round(tol.dollars * 100);
  // Everything scaled by 100 so 1% of the estimate is an INTEGER (estC x basis points / 100
  // would be a fraction): flag when 100 x gap > max(100 x floor, estimate x pct-in-percent).
  const pctPts = Math.round(tol.pct * 100);
  const thresholdX100 = Math.max(floorC * 100, estC * pctPts);
  return { threshold: Math.round(thresholdX100 / 100) / 100, flag: diffC * 100 > thresholdX100 };
}

export function compareEstimateToPaid(estimate, paidLines, tol = ESTIMATE_TOLERANCE) {
  const e = estimate || {};
  const est = num(e.estimated_indemnity);
  const lines = (paidLines || []).filter(Boolean);
  const paid = lines.length
    ? lines.reduce((s, l) => s + (num(l.aip_indemnity) || 0), 0) : null;

  const base = {
    estimate: est, paid, lines: lines.length,
    ruleVersion: e.rule_version || null,
    estimatedAt: e.estimated_at || null,
    // Surfaced on every result so a reader never has to infer it.
    forecast: e.payment_known_at_estimate !== true,
  };

  if (est == null) return { ...base, verdict: ESTIMATE.NO_ESTIMATE,
    reason: "no estimate was recorded for this claim before it was paid" };
  if (paid == null) return { ...base, verdict: ESTIMATE.PENDING,
    reason: "the carrier has not paid this claim yet — there is nothing to compare against" };

  const diff = paid - est;
  const pct = est !== 0 ? diff / Math.abs(est) : null;
  const material = estimateFlagThreshold(est, diff, tol).flag;

  if (e.payment_known_at_estimate === true) {
    return { ...base, diff, pct, verdict: ESTIMATE.NOT_A_FORECAST,
      reason: "this estimate was recorded after the payment was already on file, so it is not "
            + "evidence of a prediction — the figures are shown, the comparison is not claimed" };
  }
  return { ...base, diff, pct, material,
    verdict: material ? ESTIMATE.DISCREPANCY : ESTIMATE.AGREES,
    reason: material ? null
      : "within the greater of $" + tol.dollars + " or " + Math.round(tol.pct * 100)
        + "% of what we expected" };
}

// The same sentence discipline as the worksheet check: both figures, both sources, no verdict
// about who is right. "we expected X, they paid Y" is checkable.
export function describeEstimate(c) {
  if (!c) return null;
  const money = (v) => (v == null ? "—" : "$" + Math.round(v).toLocaleString());
  if (c.verdict === ESTIMATE.NO_ESTIMATE || c.verdict === ESTIMATE.PENDING) return c.reason;
  const head = "we expected " + money(c.estimate) + ", the carrier paid " + money(c.paid)
    + " — a difference of " + money(Math.abs(c.diff))
    + (c.pct != null && isFinite(c.pct) ? " (" + (c.pct * 100).toFixed(0) + "%)" : "");
  return c.verdict === ESTIMATE.NOT_A_FORECAST ? head + " · " + c.reason : head;
}

// One sentence a human can check, naming both figures and both sources. Deliberately NOT a
// judgement: "paid $12,799 against a stated guarantee of $44,134" is checkable; "underpaid
// by $31,335" is a conclusion this tool has no standing to reach.
export function describeCheck(c) {
  if (!c) return null;
  if (c.verdict === VERDICT.NOT_CHECKABLE) return "Not checkable — " + (c.reason || "no reason recorded");
  if (c.verdict === VERDICT.AGREES) return "Agrees";
  // ⚠ A LINE IS NOT A CHECK, AND NEITHER IS A CLAIM. `crossCheckBook` returns `lines` and
  // `claims` that both carry `verdict: "discrepancy"` and neither carries the two figures
  // `a`/`b` — only a CHECK does. Reading `c.b.label` off one of those threw a TypeError,
  // and measured against the live worksheet that is EVERY row of the worklist: all 40
  // discrepancy lines and all 13 flagged claims.
  //
  // The one shipped call site passes a check, so no screen crashes today — but this is
  // exported under a name that invites a line, and a TypeError inside a render is the
  // crash class this codebase has already shipped more than once. So it describes the
  // worst check a line holds, and where there is no check to describe it SAYS so rather
  // than inventing figures or throwing.
  if (!c.a || !c.b) {
    const inner = (c.checks || []).filter((k) => k && k.verdict === VERDICT.DISCREPANCY && k.a && k.b);
    if (inner.length) {
      const worst = inner.slice().sort((x, y) => Math.abs(y.diff || 0) - Math.abs(x.diff || 0))[0];
      return (worst.label ? worst.label + ": " : "") + describeCheck(worst);
    }
    return "Discrepancy — the two figures are not recorded on this row";
  }
  const money = (v) => (v == null ? "—" : "$" + Math.round(v).toLocaleString());
  return `${c.b.label} ${money(c.b.value)} against ${c.a.label} ${money(c.a.value)}`
       + ` — a difference of ${money(Math.abs(c.diff))}`
       + (c.ratio != null && isFinite(c.ratio) ? ` (${c.ratio.toFixed(2)}x)` : "");
}
